import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { getEmployee, updateEmployee } from '@/apis/employees'
import { getLanguages } from '@/apis/languages'
import routes from '@/constants/routes'
import useNotification from '@/hooks/useNotification'
import {
  GetEmployeeRequest,
  GetEmployeeResponse,
  UpdateEmployeeRequest,
  UpdateEmployeeResponse,
} from '@/types/apis/employees'
import { GetLanguagesResponse } from '@/types/apis/languages'

export const useUpdateEmployees = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const openNotification = useNotification()
  const [loading, setLoading] = useState(false)
  const [response, setResponse] = useState<UpdateEmployeeResponse>()

  const update = useCallback(
    async (request: UpdateEmployeeRequest) => {
      try {
        setLoading(true)
        const res = await updateEmployee(request)
        setResponse(res)
        openNotification({ description: t('pages.employees.update.updateSuccessfully') })
        navigate(routes.employees.path)
      } finally {
        setLoading(false)
      }
    },
    [navigate, openNotification, t]
  )

  return {
    loading,
    updateEmployee: update,
    response,
  }
}

export const useGetEmployee = () => {
  const [loading, setLoading] = useState(false)
  const [response, setResponse] = useState<GetEmployeeResponse>()

  const get = useCallback(async (request: GetEmployeeRequest) => {
    try {
      setLoading(true)
      const res = await getEmployee(request)
      setResponse(res)
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    loading,
    getEmployee: get,
    response,
  }
}

export const useGetLanguages = () => {
  const [getLanguagesResponse, setLanguagesResponse] = useState<GetLanguagesResponse>()

  const get = useCallback(async () => {
    const res = await getLanguages({})
    setLanguagesResponse(res)
  }, [])

  return {
    getLanguages: get,
    getLanguagesResponse,
  }
}
